import React, { useState, useRef } from 'react';
import {
  List,
  Datagrid,
  TextField,
  Button,
  useRecordContext,
  TopToolbar,
  useNotify,
  useRefresh,
  Confirm
} from 'react-admin';
import { Link } from 'react-router-dom';
import { Book as BookIcon, VideoLibrary as VideoIcon, Mic as PodcastIcon, PictureAsPdf as PdfIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { Switch, Tooltip } from '@mui/material';
import TextbookViewer from './components/TextbookViewer';

// Custom component for the description field with truncation and tooltip
const DescriptionField = () => {
  const record = useRecordContext();
  const description = record.description || '';
  const truncatedDescription = description.length > 100 ? description.substring(0, 100) + '...' : description;

  if (!description) {
    return <span style={{ color: '#999', fontStyle: 'italic' }}>No description</span>;
  }

  return (
    <Tooltip title={description} placement="top">
      <span style={{ cursor: 'help' }}>{truncatedDescription}</span>
    </Tooltip>
  );
};

// Custom field to display last updated as yyyy-mm-dd H:m
const LastUpdatedField = () => {
  const record = useRecordContext();
  const value = record?.updated_at;
  if (!value) return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
  const d = new Date(value);
  if (isNaN(d.getTime())) return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const H = String(d.getHours()); // no pad for H per requirement
  const m = String(d.getMinutes()).padStart(2, '0');
  const formatted = `${yyyy}-${mm}-${dd} ${H}:${m}`;
  return <span>{formatted}</span>;
};

// Custom field to display created at as yyyy-mm-dd H:m
const CreatedAtField = () => {
  const record = useRecordContext();
  const value = record?.created_at;
  if (!value) return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
  const d = new Date(value);
  if (isNaN(d.getTime())) return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const H = String(d.getHours()); // no pad for H per requirement
  const m = String(d.getMinutes()).padStart(2, '0');
  const formatted = `${yyyy}-${mm}-${dd} ${H}:${m}`;
  return <span>{formatted}</span>;
};

// Custom component for the status toggle switch
const StatusToggle = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  const handleStatusChange = async (event) => {
    const newStatus = event.target.checked ? 'final' : 'draft';
    setLoading(true);

    try {
      const backendBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
      const response = await fetch(
        `${backendBase}/api/admin/course_material_content/${record.material_id}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': localStorage.getItem('userId') || '91a37b17-10ce-41c2-860d-890605802763'
          },
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (response.ok) {
        // Update the record in place
        record.status = newStatus;
        notify(`Status updated to ${newStatus.toUpperCase()}`, { type: 'success' });
        // Refresh the list so updated_at reflects immediately
        refresh();
      } else {
        notify('Failed to update status', { type: 'error' });
      }
    } catch (error) {
      notify('Error updating status', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Switch
      checked={record.status === 'final'}
      onChange={handleStatusChange}
      disabled={loading}
      size="small"
    />
  );
};

// Custom component for the status toggle only
const StatusField = () => {
  return <StatusToggle />;
};

// Upload button component for video/podcast
const UploadMediaButton = ({ mediaType, label }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  if (!record) return null;

  const onPickFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = null; // reset
    fileInputRef.current?.click();
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // PDF for 'pdf', MP4 for video/podcast
    if (mediaType === 'pdf') {
      const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
      if (!isPdf) {
        notify('Only PDF files are allowed', { type: 'warning' });
        return;
      }
    } else {
      const isMp4 = file.name.toLowerCase().endsWith('.mp4') || file.type === 'video/mp4';
      if (!isMp4) {
        notify('Only MP4 files are allowed', { type: 'warning' });
        return;
      }
    }

    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
      const url = `${apiBase}/api/admin/course_material_content/${record.material_id}/upload_media?media_type=${encodeURIComponent(mediaType)}`;
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'X-User-Id': localStorage.getItem('userId') || '91a37b17-10ce-41c2-860d-890605802763'
        },
        body: formData
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Failed to upload ${mediaType}`);
      }

      const data = await res.json();
      notify(`${label} uploaded: ${data.filename}`, { type: 'success' });
      refresh();
    } catch (err) {
      notify(err.message || `Error uploading ${label}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const isPdf = mediaType === 'pdf';
  const pdfPresent = isPdf && !!record.has_pdf;
  const icon = isPdf ? <PdfIcon /> : mediaType === 'video' ? <VideoIcon /> : <PodcastIcon />;
  const buttonLabel = loading ? 'Uploading...' : pdfPresent ? 'Replace PDF' : label;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={isPdf ? 'application/pdf,.pdf' : 'video/mp4'}
        style={{ display: 'none' }}
        onChange={onFileChange}
      />
      <Button
        onClick={onPickFile}
        label={buttonLabel}
        variant={pdfPresent ? 'contained' : 'outlined'}
        size="small"
        startIcon={icon}
        disabled={loading}
      />
    </>
  );
};

// Remove uploaded PDF (reverts the student view to the HTML textbook)
const RemovePdfButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!record || !record.has_pdf) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const backendBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
      const res = await fetch(
        `${backendBase}/api/admin/course_material_content/${record.material_id}/media?media_type=pdf`,
        {
          method: 'DELETE',
          headers: {
            'X-User-Id': localStorage.getItem('userId') || '91a37b17-10ce-41c2-860d-890605802763'
          }
        }
      );
      if (res.ok) {
        record.has_pdf = false;
        notify('PDF removed. Students will see the HTML textbook.', { type: 'success' });
        refresh();
      } else {
        notify('Failed to remove PDF', { type: 'error' });
      }
    } catch (err) {
      notify('Error removing PDF', { type: 'error' });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        label="Remove PDF"
        variant="outlined"
        color="error"
        size="small"
        startIcon={<DeleteIcon />}
      />
      <Confirm
        isOpen={open}
        loading={loading}
        title="Remove PDF Textbook"
        content="Remove the uploaded PDF? Students will go back to reading the HTML textbook for this course."
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

// Delete button with confirmation
const DeleteMaterialButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const backendBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
      const response = await fetch(`${backendBase}/api/admin/course_material_content/${record.material_id}`, {
        method: 'DELETE',
        headers: {
          'X-User-Id': localStorage.getItem('userId') || '91a37b17-10ce-41c2-860d-890605802763'
        }
      });

      if (response.ok) {
        notify('Course material deleted successfully', { type: 'success' });
        refresh();
      } else {
        notify('Failed to delete course material', { type: 'error' });
      }
    } catch (error) {
      notify('Error deleting course material', { type: 'error' });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleClick}
        label="Delete"
        variant="outlined"
        color="error"
        size="small"
        startIcon={<DeleteIcon />}
      />
      <Confirm
        isOpen={open}
        loading={loading}
        title="Delete Course Material"
        content="Are you sure you want to delete this course material? This will cascade delete all associated questions and exams."
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};

// Edit button navigating to TextbookEditor
const EditButton = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Button
      component={Link}
      to={`/textbook-editor/${record.material_id}`}
      label="Edit"
      variant="outlined"
      size="small"
      startIcon={<EditIcon />}
    />
  );
};

// Actions column: textbook + video + podcast + delete
const ActionsField = () => {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <EditButton />
      <TextbookButton />
      <UploadMediaButton mediaType="video" label="Video" />
      <UploadMediaButton mediaType="podcast" label="Podcast" />
      <UploadMediaButton mediaType="pdf" label="PDF" />
      <RemovePdfButton />
      <DeleteMaterialButton />
    </div>
  );
};

// Custom component for the textbook button
const TextbookButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(false);

  const handleViewTextbook = async () => {
    const materialId = record.html_material_id || record.material_id;
    setLoading(true);

    try {
      // Use dataProvider to make authenticated request
      const backendBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
      const response = await fetch(`${backendBase}/api/admin/course_material_content/${materialId}/html`, {
        headers: {
          'X-User-Id': localStorage.getItem('userId') || '91a37b17-10ce-41c2-860d-890605802763'
        }
      });

      if (response.ok) {
        const html = await response.text();
        setHtmlContent(html);
        setDialogOpen(true);
      } else {
        notify('Failed to load textbook content', { type: 'error' });
      }
    } catch (error) {
      notify('Error loading textbook', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setHtmlContent('');
  };

  // Only show the button if the course has HTML content
  if (!record.has_html) {
    return <span style={{ color: '#999' }}>No textbook available</span>;
  }

  return (
    <>
      <Button
        onClick={handleViewTextbook}
        label={loading ? "Loading..." : "Textbook"}
        variant="contained"
        size="small"
        startIcon={<BookIcon />}
        disabled={loading}
      />

      <TextbookViewer
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={record.title}
        htmlContent={htmlContent}
        loading={loading}
        chapterSrcUrl={`${process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000'}/api/student/courses/${record.id}/textbook/chapter/1`}
      />
    </>
  );
};

// Custom actions for the list toolbar - REMOVE EXPORT
const ListActions = () => (
  <TopToolbar>
    {/* Export Button Removed */}
  </TopToolbar>
);

// Main list component
export const CourseTextbookList = () => (
  <>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px' }}>
      <h2 style={{ margin: 0 }}>Course Textbooks</h2>
    </div>
    <List
      resource="course_material_content"
      title="Course Textbooks"
      actions={<ListActions />}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="category" label="Category" />
        <TextField source="title" label="Course Title" />
        <TextField source="complexity" label="Complexity" />
        <DescriptionField label="Description" />
        <CreatedAtField label="Created At" />
        <LastUpdatedField label="Last Updated" />
        <StatusField label="Status" />
        <ActionsField label="Actions" />
      </Datagrid>
    </List>
  </>
);

// Export the resource configuration
const CourseTextbookResources = {
  list: CourseTextbookList,
};

export default CourseTextbookResources;
